import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { cartService } from '@/services/cart.service';

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const [cart, setCart] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [miniCartOpen, setMiniCartOpen] = useState(false);

  const openMiniCart = useCallback(() => setMiniCartOpen(true), []);
  const closeMiniCart = useCallback(() => setMiniCartOpen(false), []);

  const applyResponse = useCallback((res) => {
    if (!res) return;
    setCart(res.cart || null);
    setPreview(res.preview || null);
  }, []);

  const refresh = useCallback(async (redeemPoints = 0) => {
    if (!isCustomer) { setCart(null); setPreview(null); return null; }
    setLoading(true);
    try {
      const res = await cartService.getCart(redeemPoints);
      applyResponse(res);
      return res;
    } catch (e) {
      return null;
    } finally {
      setLoading(false);
    }
  }, [isCustomer, applyResponse]);

  useEffect(() => {
    if (isCustomer) {
      refresh().then(() => {
        // Completa un'aggiunta "in sospeso" avviata da utente non loggato (add -> login -> auto-add).
        try {
          const pending = sessionStorage.getItem('sila_pending_add');
          if (pending) {
            sessionStorage.removeItem('sila_pending_add');
            let p = null;
            try { p = JSON.parse(pending); } catch (e) { p = null; }
            const done = (p && p.type === 'booking')
              ? cartService.addBooking(p.kind, p.payload).then(applyResponse)
              : (p && p.type === 'product')
              ? cartService.addProduct(p).then(applyResponse)
              : cartService.addItem(pending).then(applyResponse);
            done.then(() => setMiniCartOpen(true)).catch(() => {});
          }
        } catch (e) { /* sessionStorage non disponibile */ }
      });
    } else { setCart(null); setPreview(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomer]);

  const count = useMemo(
    () => (cart?.items || []).reduce((s, it) => s + Number(it.qty || 0), 0),
    [cart],
  );

  const addItem = useCallback(async (catalogId, qty = 1) => {
    const r = await cartService.addItem(catalogId, qty); applyResponse(r); return r;
  }, [applyResponse]);
  const addBooking = useCallback(async (kind, payload) => {
    const r = await cartService.addBooking(kind, payload); applyResponse(r); return r;
  }, [applyResponse]);
  const addStay = useCallback(async (payload) => {
    const r = await cartService.addStay(payload); applyResponse(r); return r;
  }, [applyResponse]);
  const addTable = useCallback(async (payload) => {
    const r = await cartService.addTable(payload); applyResponse(r); return r;
  }, [applyResponse]);
  const addProduct = useCallback(async (payload) => {
    const r = await cartService.addProduct(payload); applyResponse(r); return r;
  }, [applyResponse]);
  const applyCoupon = useCallback(async (ownerId, code) => {
    const r = await cartService.applyCoupon(ownerId, code); applyResponse(r); return r;
  }, [applyResponse]);
  const removeCoupon = useCallback(async (ownerId) => {
    const r = await cartService.removeCoupon(ownerId); applyResponse(r); return r;
  }, [applyResponse]);
  const updateItem = useCallback(async (itemId, qty) => {
    const r = await cartService.updateItem(itemId, qty); applyResponse(r); return r;
  }, [applyResponse]);
  const removeItem = useCallback(async (itemId) => {
    const r = await cartService.removeItem(itemId); applyResponse(r); return r;
  }, [applyResponse]);
  const clear = useCallback(async () => {
    const r = await cartService.clear(); applyResponse(r); return r;
  }, [applyResponse]);

  const value = {
    cart, preview, count, loading, isCustomer,
    refresh, addItem, addBooking, addStay, addTable, addProduct, applyCoupon, removeCoupon,
    updateItem, removeItem, clear, applyResponse,
    miniCartOpen, setMiniCartOpen, openMiniCart, closeMiniCart,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

